import { JSXInternal } from "preact/src/jsx";
import { _Product, _Variant } from "./global-stores";
import { _Product_liquid, _Variant_liquid } from "./_types";
import { useMemo } from "react";
import { getVariantDisplayTitle } from "./variant-helpers";

interface Props {
  product: _Product | _Product_liquid;
  variant: _Variant | _Variant_liquid;
  checked?: boolean;
  displayPrice?: boolean;
  gridColumnsDesktop?: string;
  showProductTitle?: boolean;
  onClick?: JSXInternal.MouseEventHandler<HTMLButtonElement>;
}

export const ProductItemImageCheckbox = ({
  product,
  variant,
  checked,
  displayPrice,
  gridColumnsDesktop = "5",
  showProductTitle,
  onClick,
}: Props) => {
  // Calculate display title once before render (no need for useMemo - simple calculation)
  const variantDisplayTitle = getVariantDisplayTitle(product, variant);

  const imageToUse = useMemo(() => {
    const variantFeaturedImage = (variant as any).featured_image;

    if (variantFeaturedImage) {
      return {
        src: variantFeaturedImage.src,
        alt: variantFeaturedImage.alt,
      };
    }

    if (variant.featured_media) {
      return {
        src: variant.featured_media.src,
        alt: variant.featured_media.alt,
      };
    }

    if (variant.image) {
      return {
        src: variant.image,
        alt: variant.title,
      };
    }

    if (product.featured_image) {
      return {
        src: product.featured_image,
        alt: product.title,
      };
    }

    return {
      src: null,
      alt: product.title,
    };
  }, [product, variant]);

  return (
    <button
      key={`${product.id}-${variant.id}`}
      className={"flex flex-col items-center gap-2 text-center"}
      onClick={onClick}
    >
      <div
        className={`relative aspect-1 h-auto w-full overflow-hidden rounded-lg border-solid bg-gray-100 ${checked ? "border-[3px] border-primary-bg" : "border-[1px] border-gray-300"}`}
      >
        <img
          className={"aspect-1 h-auto w-full object-cover"}
          width={200}
          height={200}
          src={imageToUse.src}
          alt={imageToUse.alt || ""}
        />
      </div>
      <div className={`flex flex-col gap-1 ${Number(gridColumnsDesktop) >= 6 ? "text-xs" : "text-sm"}`}>
        <p className={showProductTitle ? "font-semibold" : ""}>{variantDisplayTitle}</p>
        {showProductTitle && <p>{product.title}</p>}
        {displayPrice && (
          <>
            {variant.compare_at_price && variant.compare_at_price > variant.price && (
              <span className={"text-xs text-gray-500 line-through"}>
                {window.formatMoney(variant.compare_at_price)}
              </span>
            )}
            <span className={"text-xs"}>
              {variant.price > 0 ? window.formatMoney(variant.price) : "FREE"}
            </span>
          </>
        )}
      </div>
    </button>
  );
};
